import React from 'react';
import ReactDOM from 'react-dom';
import lodashRange from 'lodash/range';
import lodashConcat from 'lodash/concat';
import moment from 'moment';
import Popover from 'material-ui/Popover';
import {select, mouse} from 'd3-selection';
import {scaleLinear, scaleTime} from 'd3-scale';
import {bisectRight, extent} from 'd3-array';
import {line as d3line} from 'd3-shape';
import {interpolatePath} from 'd3-interpolate-path';
import {axisBottom, axisLeft} from 'd3-axis';
import {timeFormat} from 'd3-time-format';
import {timeDay} from 'd3-time';
import {transform as d3Transform} from 'd3-transform';
import {format as d3Format} from 'd3-format';
import './TwoLineChart.styl';

const LINE_CHART_HEIGHT_PADDING = 20;
const LINE_CHART_AXIS_PADDING = 60;
const LINE_CHART_Y_AXIS_MAX_NUMBER_TICKS = 7;
const MILLISECONDS_IN_SEVEN_DAYS = 3600 * 24 * 1000 * 7;
const X_AXIS_TICK_INTERVAL_IN_DAYS = 7;
const LINE_SCALE = 0.95;

const LINE_CHART_BACKGROUND_COLOR = '#f7f8fa';
const LINE_CHART_AXIS_FONT_SIZE = 18;
const LINE_CHART_AXIS_FONT_FAMILY = 'Roboto, sans-serif';
const LINE_CHART_AXIS_COLOR = '#70829b';
const LINE_CHART_LINE_1_COLOR = '#4d7bee';
const LINE_CHART_LINE_2_COLOR = '#d46636';
const LINE_CHART_FOCUS_GROUP_WIDTH = 2;
const LINE_CHART_X_HOVER_LINE_MARGIN_BOTTOM = 10;
const LINE_CHART_CIRCLE_RADIUS = 8;

const LINE_1_CLS = 'line-1';
const LINE_2_CLS = 'line-2';

const lineFocusCls = cls => `${cls}-focus`;
const hoverLineCls = cls => `x-${cls}-hover-line`;
const circleCls = cls => `x-${cls}-circle`;

/**
 * Draw a two-line chart with different stroke colors. For example, the first line is `trend` and the second line is
 * `pivot` when this component is used inside `TrendPivotChart.js`
 *
 * @deprecated use LineChart/LineSeries/anything in ev-charts instead
 */
class TwoLineChart extends React.PureComponent {
  constructor() {
    super(...arguments);
    this.state = {};
  }
  componentDidMount() {
    const svg = select(this.svg);
    // svg-path for line-1
    svg.append('svg:path').attr('class', LINE_1_CLS);
    // svg-path for line-2
    svg.append('svg:path').attr('class', LINE_2_CLS);
    // svg-group for axis y
    svg.append('g').attr('class', 'y-axis');
    // svg-group for axis x
    svg.append('g').attr('class', 'x-axis');
    // svg-group for mouse focus on the first line
    const firstFocus = svg.append('g').attr('class', lineFocusCls(LINE_1_CLS));
    // left-border-line, e.g., the x-axis indicator line for dates
    firstFocus.append('line').attr('class', `${hoverLineCls(LINE_1_CLS)} hover-line`);
    // circle for the 1st line
    firstFocus
      .append('circle')
      .attr('class', `${circleCls(LINE_1_CLS)} circle`)
      .attr('r', LINE_CHART_CIRCLE_RADIUS);
    // svg-group for mouse focus on the 2nd line
    const secondFocus = svg.append('g').attr('class', lineFocusCls(LINE_2_CLS));
    // circle for the 2nd line
    secondFocus
      .append('circle')
      .attr('class', `${circleCls(LINE_2_CLS)} circle`)
      .attr('r', LINE_CHART_CIRCLE_RADIUS);
    // svg-rect for all mouse events
    svg.append('rect').attr('class', 'overlay');
    setTimeout(() => this.renderData());
  }
  componentDidUpdate(prevProps) {
    this.renderData();
  }
  renderData() {
    const svg = select(this.svg);
    const line1 = this.props.line1;
    const line2 = this.props.line2;
    // Draw the lines
    const lineTransform = d3Transform()
      .translate(d => [LINE_CHART_AXIS_PADDING, 0])
      .scale(d => LINE_SCALE);
    let fullLineData = line1;
    if (this._hasLineData(line2)) {
      fullLineData = TwoLineChart._mergeLine1AndLine2(line1, line2);
    }
    if (this._hasLineData(line1)) {
      this._drawLine1(line1, fullLineData, svg, lineTransform);
    }
    this._drawLine2(line2, fullLineData, svg, lineTransform);
    if (this._hasLineData(fullLineData)) {
      this._drawXYAxis(fullLineData, svg, lineTransform);
    }
    const rect = svg.select('.overlay');
    // Construct at most 2 mouse event handlers to handle mouse-over
    const mouseMoveEventHandlers = [];
    mouseMoveEventHandlers.push(
      this._drawMouseFocus(
        svg,
        line1,
        fullLineData,
        `.${lineFocusCls(LINE_1_CLS)}`,
        `.${hoverLineCls(LINE_1_CLS)}`,
        `.${circleCls(LINE_1_CLS)}`,
        rect
      )
    );
    const circleClsName = `.${circleCls(LINE_2_CLS)}`;
    if (this._hasLineData(line2)) {
      svg.select(`.${circleCls(LINE_2_CLS)}`).attr('display', null);
      mouseMoveEventHandlers.push(
        this._drawMouseFocus(
          svg,
          line2,
          fullLineData,
          `.${lineFocusCls(LINE_2_CLS)}`,
          null,
          circleClsName,
          rect
        )
      );
    } else {
      svg.select(circleClsName).attr('display', 'none');
    }
    // Call each mouse event handler in-turn within the `rect` overlay
    this._handleMouseEventOverFocus(svg, rect, mouseMoveEventHandlers);
  }

  _hasLineData(data) {
    return data && data.length > 0;
  }

  _drawPopovers(svg) {
    const self = this;
    const {popoverContent} = this.props;
    const rect = svg.select('.overlay');
    const {line2Field, line2, line1, line1MessageFunc} = this.props;
    svg.select(`.${lineFocusCls(LINE_1_CLS)}`).each(function () {
      const {data: line1Data} = TwoLineChart._dataForMouse(line1, rect);
      const line1Message = line1MessageFunc(line2Field, line1Data.value);
      let percentageDiff = null;
      let line2Message = null;
      if (line2Field && line2) {
        const {data: line2Data} = TwoLineChart._dataForMouse(line2, rect);
        const line1Calls = line1Data ? line1Data.value : 0;
        const line2Calls = line2Data ? line2Data.value : 0;
        line2Message = line2Field ? `Without ${line2Field} Filter (${line2Calls} calls)` : null;
        percentageDiff = Math.abs(line1Calls - line2Calls) / Math.max(line1Calls, line2Calls, 1);
      }

      self.setState({
        tooltipContent: popoverContent(
          line1Data,
          line1Message,
          line2Message,
          line2Field,
          percentageDiff
        ),
        hoveredEl: this,
      });
    });
  }

  _handleMouseEventOverFocus(svg, rect, mouseMoveEventHandlers) {
    const svgDOM = ReactDOM.findDOMNode(this.svg);
    const width = svgDOM.clientWidth;
    const height = svgDOM.clientHeight;
    const line1Focus = svg.select(`.${lineFocusCls(LINE_1_CLS)}`);
    const line2Focus = svg.select(`.${lineFocusCls(LINE_2_CLS)}`);
    const rectOverlayTransform = d3Transform().translate(() => [LINE_CHART_AXIS_PADDING, 0]);
    rect
      .attr('transform', rectOverlayTransform)
      .attr('width', width * LINE_SCALE)
      .attr('height', height);
    const lines = [line1Focus, line2Focus];
    rect
      .on('mouseover', () => {
        lines.forEach(line => line.style('display', null));
      })
      .on('mouseout', () => {
        this.setState({hoveredEl: null});
        lines.forEach(line => line.style('display', 'none'));
      })
      .on('mousemove', () => {
        mouseMoveEventHandlers.forEach(eventHandler => eventHandler());
        this._drawPopovers(svg);
      });
  }

  _drawMouseFocus(svg, data, fullLineData, focusCls, hoverLineCls, circleCls, rect) {
    const focusTransform = d3Transform().translate(() => [LINE_CHART_AXIS_PADDING, 0]);
    const focus = svg
      .select(focusCls)
      // .style('display', 'none')
      .attr('width', LINE_CHART_FOCUS_GROUP_WIDTH);
    if (hoverLineCls) {
      focus.select(hoverLineCls).attr('transform', focusTransform);
    }

    return () => {
      const {data: d, x} = TwoLineChart._dataForMouse(data, rect);
      const xTransformValue = x(d.date);
      const transform = d3Transform().translate(() => [xTransformValue, 0]);
      focus.attr('transform', transform);
      // Scale the line-1-circle properly
      const y = this._yScale(fullLineData, [
        rect.attr('height') - LINE_CHART_HEIGHT_PADDING - LINE_CHART_X_HOVER_LINE_MARGIN_BOTTOM,
        0,
      ]);
      const dValue = y(d.value * (LINE_SCALE - 0.05));
      const fillColor =
        focusCls === `.${lineFocusCls(LINE_1_CLS)}`
          ? LINE_CHART_LINE_1_COLOR
          : LINE_CHART_LINE_2_COLOR;
      focus
        .select(circleCls)
        .attr('fill', fillColor)
        .attr('cx', LINE_CHART_AXIS_PADDING)
        .attr('cy', dValue);
      if (hoverLineCls) {
        // Scale the line-1-hover-line properly
        const fullY = this._yScale(fullLineData, [
          rect.attr('height') - LINE_CHART_HEIGHT_PADDING,
          0,
        ]);
        const fullDValue = fullY(0);
        focus
          .select(hoverLineCls)
          .attr('y1', LINE_CHART_HEIGHT_PADDING)
          .attr('y2', fullDValue - LINE_CHART_X_HOVER_LINE_MARGIN_BOTTOM);
      }

      focus.style('display', 'block');
    };
  }

  static _dataForMouse(data, rect) {
    const x = TwoLineChart._xScale(data, rect.attr('width'));
    const x0 = x.invert(mouse(rect.node())[0]);
    const dates = data.map(d => d.date);
    const idx = bisectRight(dates, x0, 1);
    const d0 = data[idx - 1],
      d1 = data[idx];
    return {
      data:
        d1 && TwoLineChart._diffDates(x0, d0.date) > TwoLineChart._diffDates(d1.date, x0) ? d1 : d0,
      x: x,
    };
  }

  static _diffDates(end, start) {
    const duration = moment.duration(moment(end).diff(moment(start)));
    return Math.abs(duration.asHours());
  }

  /** Merge line1 and line2 data so that the x-y-axis drawing takes into account both of them */
  static _mergeLine1AndLine2(line1, line2) {
    return lodashConcat(line1, line2);
  }

  _drawLine2(data, fullLineData, svg, lineTransform) {
    this._drawLine(
      data,
      fullLineData,
      svg,
      lineTransform,
      `.${LINE_2_CLS}`,
      LINE_CHART_LINE_2_COLOR
    );
  }

  _drawLine1(data, fullLineData, svg, lineTransform) {
    this._drawLine(
      data,
      fullLineData,
      svg,
      lineTransform,
      `.${LINE_1_CLS}`,
      LINE_CHART_LINE_1_COLOR
    );
  }

  _drawLine(data, fullLineData, svg, transform, lineCls, lineColor) {
    const self = this;
    svg
      .select(lineCls)
      .attr('transform', transform)
      .attr('stroke', lineColor)
      .transition()
      .duration(1000)
      .attrTween('d', function (d) {
        let prev = select(this).attr('d');
        let lineFunc = self._d3LineForData(data, fullLineData);
        let cur = lineFunc(data);
        return interpolatePath(prev, cur);
      });
  }

  _drawXYAxis(data, svg, transform) {
    const svgDOM = ReactDOM.findDOMNode(this.svg);
    const yAxisTransform = d3Transform()
      .translate(d => [LINE_CHART_AXIS_PADDING, 0])
      .scale(d => LINE_SCALE);
    // Draw the y-axis
    svg
      .select('.y-axis')
      .attr('transform', yAxisTransform)
      .attr('font-size', LINE_CHART_AXIS_FONT_SIZE)
      .attr('font-family', LINE_CHART_AXIS_FONT_FAMILY)
      .attr('stroke', LINE_CHART_AXIS_COLOR)
      .call(this._yAxisFunc(svg, data));
    // Draw the x-axis
    const xAxisTransform = transform.translate(d => [
      0,
      svgDOM.clientHeight - LINE_CHART_HEIGHT_PADDING,
    ]);
    svg
      .select('.x-axis')
      .attr('transform', xAxisTransform)
      .attr('font-size', LINE_CHART_AXIS_FONT_SIZE)
      .attr('font-family', LINE_CHART_AXIS_FONT_FAMILY)
      .attr('stroke', LINE_CHART_AXIS_COLOR)
      .call(this._xAxisFunc(data));
  }

  _yAxisFunc(svg, data) {
    const y = this._yScale(data);
    const yExtent = extent(data, d => d.value);
    let tickRange = yExtent[1] - yExtent[0];
    let step =
      tickRange <= LINE_CHART_Y_AXIS_MAX_NUMBER_TICKS
        ? 1
        : tickRange / LINE_CHART_Y_AXIS_MAX_NUMBER_TICKS;
    const tickValues = lodashRange(yExtent[0], yExtent[1] + 1, step);
    return axisLeft(y).tickValues(tickValues).tickFormat(d3Format('d')).tickSizeOuter(0);
  }

  _xAxisFunc(data) {
    const svgDOM = ReactDOM.findDOMNode(this.svg);
    const x = TwoLineChart._xScale(data, svgDOM.clientWidth);
    // Number of ticks is number of months within the x-date-range
    const xExtent = extent(data, d => d.date);
    let ticks = xExtent[1] - xExtent[0];
    ticks = ticks / MILLISECONDS_IN_SEVEN_DAYS;
    return axisBottom(x)
      .ticks(ticks)
      .tickArguments(timeDay.every(X_AXIS_TICK_INTERVAL_IN_DAYS))
      .tickFormat(timeFormat('%b %y'))
      .tickSizeOuter(0);
  }

  _d3LineForData(data, fullLineData) {
    const svgDOM = ReactDOM.findDOMNode(this.svg);
    let x = TwoLineChart._xScale(data, svgDOM.clientWidth);
    const y = this._yScale(fullLineData);
    return d3line()
      .x(d => x(d.date))
      .y(d => y(d.value));
  }

  static _xScale(data, width) {
    return scaleTime()
      .domain(extent(data, d => d.date))
      .range([0, width * LINE_SCALE]);
  }

  _yScale(data, range = null) {
    range = range || [this.props.height - LINE_CHART_HEIGHT_PADDING, LINE_CHART_HEIGHT_PADDING];
    return scaleLinear()
      .domain(extent(data, d => d.value))
      .nice()
      .range(range);
  }

  render() {
    const styles = {width: this.props.width, height: this.props.height};
    return (
      <div className="line-chart" style={styles}>
        <svg
          ref={ref => {
            this.svg = ref;
          }}>
          <rect width="100%" height="100%" fill={LINE_CHART_BACKGROUND_COLOR} />
        </svg>
        {this.state.hoveredEl && (
          <Popover
            className="destyle-popover no-pointer-events"
            canAutoPosition={false}
            open={true}
            useLayerForClickAway={false}
            anchorEl={this.state.hoveredEl}
            anchorOrigin={{
              horizontal: 'left',
              vertical: 'center',
            }}
            targetOrigin={{
              horizontal: 'right',
              vertical: 'center',
            }}>
            <div className="tooltip-container no-tail">
              <div className="ev-tooltip-control ev-tooltip">
                <div className="tooltip-content">{this.state.tooltipContent}</div>
              </div>
            </div>
          </Popover>
        )}
      </div>
    );
  }
}

export default TwoLineChart;
